/**
 * The theory here is that we can specify types without having a specific type for each event
 * This might be a terrible idea, but it will take FOREVER to create all the events for each permutation
 * and I think this gets us quite a ways to consistency without having to do that.
 */

import type { AnalyticsBase, FindFn, PRIMARY_POSSESSIVE } from "./base"

export function getPossessiveForPlanbook(
  findFn: FindFn,
  userId: string,
  planbookId: string | null | undefined,
): PRIMARY_POSSESSIVE {
  let planbook = planbookId ? findFn("planbook", planbookId) : null
  let user = findFn("user", userId)
  let isOwner = user && planbook ? user.id === planbook.relationships.owner.data.id : false
  return isOwner ? "owns" : "others"
}

export type AnalyticsPlanbook =
  | PlanbookCreate
  | PlanbookCustomize
  | PlanbookCustomizeToggle
  | PlanbookDelete
  | PlanbookEditCalendar
  | PlanbookEditCollaborators
  | PlanbookEditBasics
  | PlanbookEditIntegrations
  | PlanbookEditRepeatingSchedule
  | PlanbookEditTemplates
  | PlanbookPrint
  | PlanbookSearch
  | PlanbookShare
  | PlanbookView
  | PlanbookUI

export interface AnalyticsPlanbookBase extends AnalyticsBase {
  root_id: string | null
  primary_object_type: "planbook"
  primary_object_id_and_ancestor_ids: Array<string>
}

export interface PlanbookCreate extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "create"
  primary_object_specifier: "onboarding"
  // The adverbial fields are only present when copying a planbook.
  adverbial_verb: "copying" | null
  adverbial_object_type: "planbook" | null
  adverbial_object_specifier: null
}

export interface PlanbookCustomize extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "customize"
  primary_object_specifier: null
  adverbial_verb: "copying"
  adverbial_object_type: "planbook"
  adverbial_object_specifier: null
}
export interface PlanbookCustomizeToggle extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "customize"
  primary_object_specifier: "filter_classes" | "line_up_classes"
  adverbial_verb: "toggling"
  adverbial_object_type: "course" | "line_up_classes"
  adverbial_object_specifier: "on" | "off"
}
export interface PlanbookDelete extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "delete"
  object_specifier: null
  adverbial_verb: null
  adverbial_object_type: null
  adverbial_object_specifier: null
}

export interface PlanbookEditCalendar extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE | null // if it's really a rotation calendar, it's null
  primary_verb: "edit"
  primary_object_specifier: "calendar"
  adverbial_verb: "adding" | "editing" | "removing"
  adverbial_object_type:
    | "day_card"
    | "annotation"
    | "rotation"
    | "course_order"
    | "rotation_day_off"
  adverbial_object_specifier: "id" | "reset" | "skip" | "single" | "range" | null
}

export interface PlanbookEditCollaborators extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "edit"
  primary_object_specifier: "collaborators"
  adverbial_verb: "adding" | "editing" | "removing"
  adverbial_object_type: "collaborator" | "public_access"
  adverbial_object_specifier: "user" | "group" | "permissions" | "on" | "off"
}
export interface PlanbookEditBasics extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "edit"
  primary_object_specifier: "cover" | "title" | "courses"
  adverbial_verb: "reordering" | "selecting" | "searching" | "uploading" | null
  adverbial_object_type: "color" | "image" | null
  adverbial_object_specifier: null
}

export interface PlanbookEditIntegrations extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "edit"
  primary_object_specifier: "integrations"
  adverbial_verb: "adding" | "removing"
  adverbial_object_type: "google_classroom"
  adverbial_object_specifier: null
}

export interface PlanbookEditRepeatingSchedule extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "edit"
  primary_object_specifier: "repeating_schedule"
  adverbial_verb: "toggling"
  adverbial_object_type: "course"
  adverbial_object_specifier: null
}
export interface PlanbookEditTemplates extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "edit"
  primary_object_specifier: "templates"
  adverbial_verb: "adding" | "saving" | "removing"
  adverbial_object_type: "template"
  adverbial_object_specifier: null
}

export interface PlanbookPrint extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "print"
  primary_object_specifier:
    | "lesson"
    | "day"
    | "week"
    | "month"
    | "unit"
    | "timeline"
    | "outline"
    | "standards_tracker"
    | "search_page"
    | null
  adverbial_verb: null
  adverbial_object_type: null
  adverbial_object_specifier: null
}
export interface PlanbookSearch extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "search"
  primary_object_specifier: null
  adverbial_verb: "selecting" | null
  adverbial_object_type: "lesson" | null
  adverbial_object_specifier: null
}
export interface PlanbookShare extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "share"
  primary_object_type: "planbook"
  primary_object_specifier: "day" | "week" | "month" | "standards_tracker" | "timeline" | "outline"

  adverbial_verb: "emailing" | "copying" | "downloading"
  adverbial_object_type: "link"
  adverbial_object_specifier: "qr_code" | null
}
export interface PlanbookView extends AnalyticsPlanbookBase {
  primary_possessive: PRIMARY_POSSESSIVE
  primary_verb: "view"
  primary_object_specifier:
    | "day"
    | "week"
    | "month"
    | "timeline"
    | "outline"
    | "standards_tracker"
    | "sharing"
    | "class_websites"
    | "search_page"
    | null
  adverbial_verb: null
  adverbial_object_type: null
  adverbial_object_specifier: null
}

export interface PlanbookUI extends AnalyticsBase {
  date_plan_scheduled_for: null

  primary_verb: "open" | "click"
  primary_possessive: "owns"

  primary_object_type: "planbook"
  primary_object_specifier: "planbook_share_menu" | "email_link_button"
  primary_object_id_and_ancestor_ids: Array<string>

  adverbial_verb: null

  adverbial_object_type: null
  adverbial_object_id: null
  adverbial_object_specifier: null
  adverbial_object_id_and_ancestor_ids: Array<string> | null
}

// date_plan_scheduled_for: Date | nul
// course_id: string | null
// card_stack_id: string | null
// card_ids_edited: string[]
// words_added: number
// attachments_added: number
// links_added: number
// standards_added: number
// comments_added: number
// cards_added: number
